import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

// ── Admin thunks ─────────────────────────────────────────────────────────
export const admin_get_tickets = createAsyncThunk(
    'support/admin_get_tickets',
    async ({ page = 1, parPage = 20, status = '', userType = '' }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get(
                `/admin/support-tickets?page=${page}&parPage=${parPage}&status=${status}&userType=${userType}`,
                { withCredentials: true }
            )
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const admin_get_ticket = createAsyncThunk(
    'support/admin_get_ticket',
    async (ticketId, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get(`/admin/support-ticket/${ticketId}`, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const admin_update_status = createAsyncThunk(
    'support/admin_update_status',
    async ({ ticketId, status }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.put(`/admin/support-ticket/${ticketId}/status`, { status }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const admin_add_response = createAsyncThunk(
    'support/admin_add_response',
    async ({ ticketId, message, status }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post(`/admin/support-ticket/${ticketId}/response`, { message, status }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

// ── Seller thunks ────────────────────────────────────────────────────────
export const seller_create_ticket = createAsyncThunk(
    'support/seller_create_ticket',
    async (ticketData, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/seller/support-ticket', ticketData, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const seller_get_tickets = createAsyncThunk(
    'support/seller_get_tickets',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/seller/support-tickets', { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const seller_get_ticket = createAsyncThunk(
    'support/seller_get_ticket',
    async (ticketId, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get(`/seller/support-ticket/${ticketId}`, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const seller_add_response = createAsyncThunk(
    'support/seller_add_response',
    async ({ ticketId, message, userName }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post(`/seller/support-ticket/${ticketId}/response`, { message, userName }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

const STATUS_COLORS = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-500',
}

export const supportReducer = createSlice({
    name: 'support',
    initialState: {
        tickets: [],
        totalTickets: 0,
        activeTicket: null,
        successMessage: '',
        errorMessage: '',
    },
    reducers: {
        messageClear: (state) => {
            state.successMessage = ''
            state.errorMessage = ''
        },
        clearActiveTicket: (state) => {
            state.activeTicket = null
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(admin_get_tickets.fulfilled, (state, { payload }) => {
                state.tickets = payload.tickets
                state.totalTickets = payload.totalTickets
            })
            .addCase(admin_get_ticket.fulfilled, (state, { payload }) => {
                state.activeTicket = payload.ticket
            })
            .addCase(admin_update_status.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                if (state.activeTicket?._id === payload.ticket._id) {
                    state.activeTicket = payload.ticket
                }
                state.tickets = state.tickets.map(t => t._id === payload.ticket._id ? { ...t, status: payload.ticket.status } : t)
            })
            .addCase(admin_add_response.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                state.activeTicket = payload.ticket
                state.tickets = state.tickets.map(t => t._id === payload.ticket._id ? { ...t, status: payload.ticket.status } : t)
            })
            .addCase(seller_create_ticket.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                state.tickets = [payload.ticket, ...state.tickets]
                state.totalTickets += 1
            })
            .addCase(seller_get_tickets.fulfilled, (state, { payload }) => {
                state.tickets = payload.tickets
                state.totalTickets = payload.tickets.length
            })
            .addCase(seller_get_ticket.fulfilled, (state, { payload }) => {
                state.activeTicket = payload.ticket
            })
            .addCase(seller_add_response.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                state.activeTicket = payload.ticket
            })
            .addMatcher(
                (action) => action.type.startsWith('support/') && action.type.endsWith('/rejected'),
                (state, { payload }) => {
                    state.errorMessage = payload?.error || 'Something went wrong'
                }
            )
    }
})

export const { messageClear, clearActiveTicket } = supportReducer.actions
export default supportReducer.reducer
